import { cn } from 'cn';
import { PropsWithChildren } from 'react';

import { formatPhoneNumber } from '@vestigas/utils';

import { Hint, Icon } from '@vestigas/ui';

export type Contact =
  | {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      phone?: string | null;
      phoneNumber?: string | null; // we currently have both phone and phoneNumber keys in API responses...
    }
  | null
  | undefined;

function formatContactName(
  contact:
    | { firstName?: string | null; lastName?: string | null }
    | null
    | undefined,
): string {
  if (!contact) {
    return '—';
  }

  const fullName =
    `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();

  return fullName || '—';
}

export type ContactCardProps = PropsWithChildren<{
  readonly companyName?: string | null;
  readonly contact: Contact;
  readonly hint?: string;
  readonly label?: string;
  readonly renderHyperlinks?: boolean;
  readonly renderPlaceholder?: boolean;
}> &
  ComponentStyling;

export function ContactCard({
  children,
  companyName,
  className,
  contact,
  hint,
  label,
  renderHyperlinks = true,
  renderPlaceholder,
  style,
}: ContactCardProps) {
  return (
    <div className={cn('text-sm', className)} style={style}>
      {label ? <p className="font-medium">{label}</p> : null}
      {hint ? (
        <Hint className="mt-1 mb-1.5 block leading-tight">{hint}</Hint>
      ) : null}
      <div className="max-w-88 space-y-1 rounded-md border border-tiga-border-lighter bg-white px-4 py-3 font-normal text-tiga-text-default">
        {renderPlaceholder ? (
          <div className="space-y-2 py-1">
            <header>
              <div className="h-4 w-28 animate-pulse rounded bg-tiga-surface-lighter" />
            </header>
            <div className="space-y-2">
              <div className="h-3 w-36 animate-pulse rounded bg-tiga-surface-lighter" />
              <div className="h-3 w-44 animate-pulse rounded bg-tiga-surface-lighter" />
            </div>
          </div>
        ) : (
          <>
            <header>
              <strong className="block wrap-break-word">
                {formatContactName(contact)}
              </strong>
            </header>
            <div className="space-y-1">
              {companyName ? (
                <p className="font-medium wrap-break-word">{companyName}</p>
              ) : null}
              {contact?.email ? (
                renderHyperlinks ? (
                  <a
                    aria-label={`Send email to ${contact.email} (opens in new tab)`}
                    className="flex min-w-0 gap-1 hover:text-tiga-action-lighter"
                    href={`mailto:${contact.email}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <Icon
                      className="mt-0.75 shrink-0"
                      metaphor="email"
                      size="sm"
                    />
                    <span className="min-w-0 flex-1 wrap-break-word">
                      {contact.email}
                    </span>
                  </a>
                ) : (
                  <p className="flex min-w-0 items-center gap-1">
                    <Icon
                      className="mt-0.75 shrink-0"
                      metaphor="email"
                      size="sm"
                    />
                    <span className="min-w-0 flex-1 wrap-break-word">
                      {contact.email}
                    </span>
                  </p>
                )
              ) : null}
              {contact?.phone || contact?.phoneNumber ? (
                renderHyperlinks ? (
                  <a
                    className="flex min-w-0 gap-1 hover:text-tiga-action-lighter"
                    href={`tel:${contact.phone || contact.phoneNumber}`}
                  >
                    <Icon
                      className="mt-0.75 shrink-0"
                      metaphor="phone"
                      size="sm"
                    />
                    <span className="min-w-0 flex-1 wrap-break-word">
                      {formatPhoneNumber(contact.phone || contact.phoneNumber)}
                    </span>
                  </a>
                ) : (
                  <p className="flex min-w-0 gap-1">
                    <Icon
                      className="mt-0.75 shrink-0"
                      metaphor="phone"
                      size="sm"
                    />
                    <span className="min-w-0 flex-1 wrap-break-word">
                      {formatPhoneNumber(contact.phone || contact.phoneNumber)}
                    </span>
                  </p>
                )
              ) : null}
            </div>
            {children}
          </>
        )}
      </div>
    </div>
  );
}
